"use client";

/* eslint-disable react-hooks/exhaustive-deps */

import dynamic from "next/dynamic";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { HelpButton } from "staysure-component-library";

import withBrand from "@/components/with-brand";
import {
  HELP_BUTTON_AVATAR_PATH,
  HELP_OVERLAY_NEW_ICON_PATH,
} from "@/constants";
import useWindowSize from "@/hooks/use-window-size/use-window-size";
import { getWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { RootState } from "@/store/root-reducer";

import Styles from "./help-overlay-button.module.css";
import {
  openHelpOverlay,
  closeHelpOverlay,
} from "../../../store/slices/help-overlay.slice";

const HelpOverLay = dynamic(
  () => import("./component/help-overlay/help-overlay"),
  {
    ssr: false,
  }
);

function HelpOverLayButton(
  props: Readonly<{
    brand?: "staysure" | "avanti";
    useNewHelpOverlay?: boolean;
    useAbsolutePositioning?: boolean; // Set to false to use relative positioning (for new layouts)
  }>
) {
  const { brand, useNewHelpOverlay, useAbsolutePositioning = true } = props;
  const dispatch = useDispatch();
  const [closeContent, setCloseContent] = useState<string>("");
  const [getHelpContent, setGetHelpContent] = useState<string>("");

  const windowSize = useWindowSize();

  const isOpen = useSelector((state: RootState) => state.helpOverlay.isOpen);

  useEffect(() => {
    const fetchLabel = async () => {
      const close = await getWizardTranslation("close", undefined, "Close");
      setCloseContent(close);

      const getHelp = await getWizardTranslation(
        "get_help",
        undefined,
        "Get Help"
      );
      setGetHelpContent(getHelp);
    };

    fetchLabel();
  }, []);

  const ref = React.useRef<HTMLButtonElement>(null);
  const { top, right } = useMemo(() => {
    const buttonRect = ref?.current?.getBoundingClientRect();
    const topVal = buttonRect?.bottom ?? 0;
    const rightVal = buttonRect?.right ?? 0;
    return { top: topVal, right: rightVal };
  }, [ref?.current?.getBoundingClientRect(), windowSize]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        dispatch(closeHelpOverlay());
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [dispatch]);

  const getSpacingClass = () => {
    if (!useNewHelpOverlay) return "";
    return isOpen
      ? Styles.help_button_spacing_open
      : Styles.help_button_spacing;
  };

  return (
    <div
      data-testid="help-overlay-button-wrapper-test-id"
      className={`w-[165px] h-[58px] top-[-1px] right-[-1px] text-text-functional-subtle rounded-bl-[28px] ${
        useAbsolutePositioning
          ? "bg-surface-neutral-option-2 absolute"
          : "relative"
      }`}
    >
      {isOpen && (
        <HelpOverLay
          top={top}
          right={right}
          isOpen={isOpen}
          useNewHelpOverlay={useNewHelpOverlay}
          setIsOpen={(val) => {
            setTimeout(() => {
              val ? dispatch(openHelpOverlay()) : dispatch(closeHelpOverlay());
            }, 100);
          }}
        />
      )}
      <div
        className={`relative z-[101] cursor-pointer mt-[10px] ml-[8px] hover:outline outline-offset-2 outline-interactive-focus-default outline-2 rounded-full text-transparent ${
          Styles.get_help
        } ${getSpacingClass()}`}
      >
        <HelpButton
          id="helpoverlay-automate-test-id"
          tab-index={2}
          isOpen={isOpen}
          buttonRef={ref}
          width={useNewHelpOverlay ? "30px" : "36px"}
          height={useNewHelpOverlay ? "30px" : "36px"}
          onClick={() => {
            if (isOpen) {
              dispatch(closeHelpOverlay());
            } else {
              // Close CXone chat window before opening help overlay
              if (typeof window !== "undefined" && window.minimizeCXoneChat) {
                window.minimizeCXoneChat();
              }
              dispatch(openHelpOverlay());
            }
          }}
          avatarSrc={
            useNewHelpOverlay
              ? HELP_OVERLAY_NEW_ICON_PATH
              : HELP_BUTTON_AVATAR_PATH
          }
          brand={brand}
          closeLabel={closeContent}
          openLabel={getHelpContent}
        />
      </div>
      {useAbsolutePositioning && (
        <>
          <div className={` ${Styles.curved_corner_top_right} absolute `} />
          <div className={` ${Styles.curved_corner_bottom_right} absolute `} />
        </>
      )}
    </div>
  );
}

export default withBrand(HelpOverLayButton);
